import React, { useState } from 'react';
import '../styles/companion-views.css';

/**
 * CompanionOrderList - Displays a list of orders related to current context
 */
const CompanionOrderList = ({ data = {} }) => {
  const [orders] = useState(data.orders || []);
  const [selectedOrderId, setSelectedOrderId] = useState(data.currentOrderId || null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  const handleOrderClick = (orderId) => {
    if (data.onOrderSelect) {
      data.onOrderSelect(orderId);
    }
    setSelectedOrderId(orderId);
  };

  const toggleOrderExpand = (orderId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: '⏳', color: 'warning' },
      processing: { icon: '🔄', color: 'info' },
      completed: { icon: '✅', color: 'success' },
      failed: { icon: '❌', color: 'danger' },
      cancelled: { icon: '🚫', color: 'secondary' }
    };
    return statusConfig[status] || { icon: '📄', color: 'default' };
  };

  const groupOrdersByStatus = () => {
    const grouped = {
      active: orders.filter(o => ['pending', 'processing'].includes(o.status)),
      completed: orders.filter(o => o.status === 'completed'),
      other: orders.filter(o => ['failed', 'cancelled'].includes(o.status))
    };
    return grouped;
  };

  const groupedOrders = groupOrdersByStatus();

  return (
    <div className="companion-order-list">
      {/* Summary Cards */}
      <div className="order-summary-cards">
        <div className="summary-card">
          <span className="card-value">{orders.length}</span>
          <span className="card-label">Total Orders</span>
        </div>
        <div className="summary-card active">
          <span className="card-value">{groupedOrders.active.length}</span>
          <span className="card-label">Active</span>
        </div>
        <div className="summary-card completed">
          <span className="card-value">{groupedOrders.completed.length}</span>
          <span className="card-label">Completed</span>
        </div>
      </div>

      {/* Orders List */}
      <div className="orders-container">
        {/* Active Orders */}
        {groupedOrders.active.length > 0 && (
          <div className="order-group">
            <h5 className="group-title">Active Orders</h5>
            <div className="orders-list">
              {groupedOrders.active.map((order) => {
                const statusBadge = getStatusBadge(order.status);
                const isExpanded = expandedOrders.has(order.id);
                
                return (
                  <div
                    key={order.id}
                    className={`companion-order-item ${selectedOrderId === order.id ? 'selected' : ''} ${order.status}`}
                    onClick={() => handleOrderClick(order.id)}
                  >
                    <div className="order-header">
                      <div className="order-header-left">
                        <span className={`order-status-icon ${statusBadge.color}`}>
                          {statusBadge.icon}
                        </span>
                        <div className="order-info">
                          <span className="order-id">{order.id}</span>
                          {order.name && (
                            <span className="order-name">{order.name}</span>
                          )}
                        </div>
                      </div>
                      <div className="order-header-right">
                        {selectedOrderId === order.id && (
                          <span className="current-badge">Current</span>
                        )}
                        <button
                          className="order-expand-btn"
                          onClick={(e) => toggleOrderExpand(order.id, e)}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      </div>
                    </div>

                    {/* Order Details */}
                    {isExpanded && (
                      <div className="order-details">
                        {order.createdAt && (
                          <div className="detail-row">
                            <span className="detail-label">Created:</span>
                            <span className="detail-value">{order.createdAt}</span>
                          </div>
                        )}
                        {order.provider && (
                          <div className="detail-row">
                            <span className="detail-label">Provider:</span>
                            <span className="detail-value">{order.provider}</span>
                          </div>
                        )}
                        {order.jobCount !== undefined && (
                          <div className="detail-row">
                            <span className="detail-label">Jobs:</span>
                            <span className="detail-value">{order.jobCount}</span>
                          </div>
                        )}
                        {order.progress !== undefined && (
                          <div className="order-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill"
                                style={{ width: `${order.progress}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{order.progress}%</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quick Stats */}
                    {!isExpanded && order.jobCount !== undefined && (
                      <div className="order-quick-stats">
                        <span className="quick-stat">
                          {order.jobCount} job{order.jobCount !== 1 ? 's' : ''}
                        </span>
                        {order.progress !== undefined && (
                          <span className="quick-stat">
                            {order.progress}% complete
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Completed Orders */}
        {groupedOrders.completed.length > 0 && (
          <div className="order-group">
            <h5 className="group-title">Completed Orders</h5>
            <div className="orders-list">
              {groupedOrders.completed.map((order) => {
                const statusBadge = getStatusBadge(order.status);
                
                return (
                  <div
                    key={order.id}
                    className={`companion-order-item ${selectedOrderId === order.id ? 'selected' : ''} ${order.status}`}
                    onClick={() => handleOrderClick(order.id)}
                  >
                    <div className="order-header">
                      <div className="order-header-left">
                        <span className={`order-status-icon ${statusBadge.color}`}>
                          {statusBadge.icon}
                        </span>
                        <div className="order-info">
                          <span className="order-id">{order.id}</span>
                          {order.name && (
                            <span className="order-name">{order.name}</span>
                          )}
                        </div>
                      </div>
                      <div className="order-header-right">
                        {order.completedAt && (
                          <span className="order-time">{order.completedAt}</span>
                        )}
                      </div>
                    </div>

                    {order.resultSummary && (
                      <div className="order-result-summary">
                        {order.resultSummary}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Other Orders (Failed/Cancelled) */}
        {groupedOrders.other.length > 0 && (
          <div className="order-group">
            <h5 className="group-title">Other Orders</h5>
            <div className="orders-list">
              {groupedOrders.other.map((order) => {
                const statusBadge = getStatusBadge(order.status);
                
                return (
                  <div
                    key={order.id}
                    className={`companion-order-item ${selectedOrderId === order.id ? 'selected' : ''} ${order.status}`}
                    onClick={() => handleOrderClick(order.id)}
                  >
                    <div className="order-header">
                      <div className="order-header-left">
                        <span className={`order-status-icon ${statusBadge.color}`}>
                          {statusBadge.icon}
                        </span>
                        <div className="order-info">
                          <span className="order-id">{order.id}</span>
                          <span className="order-status-text">{order.status}</span>
                        </div>
                      </div>
                    </div>
                    {order.errorMessage && (
                      <div className="order-error">
                        {order.errorMessage}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {orders.length === 0 && (
          <div className="companion-no-results">
            <span className="no-results-icon">📋</span>
            <p>No orders found</p>
            <small>Orders will appear here when available</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanionOrderList;
